import { FC, FormEvent, useEffect, useState } from "react";
import {
  createResource,
  deleteResource,
  listResource,
  updateResource,
} from "data/adminApi";
import ConfirmDialog from "components/Admin/ConfirmDialog";
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline";

interface Blog {
  id: number;
  title: string;
  content: string;
  author: string;
  published_date: string;
}

const empty = {
  title: "",
  content: "",
  author: "",
  published_date: new Date().toISOString().slice(0, 16),
};

const AdminBlogs: FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    setBlogs(await listResource<Blog>("/blogs/"));
  };

  useEffect(() => {
    load().catch(() => setMessage("Failed to load blogs."));
  }, []);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    const payload = {
      ...form,
      published_date: new Date(form.published_date).toISOString(),
    };
    try {
      if (editId) await updateResource(`/blogs/${editId}/`, payload);
      else await createResource("/blogs/", payload);
      setForm(empty);
      setEditId(null);
      await load();
      setMessage("Blog saved.");
    } catch {
      setMessage("Could not save blog.");
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    setLoading(true);
    try {
      await deleteResource(`/blogs/${deleteId}/`);
      await load();
      setMessage("Blog deleted.");
    } catch {
      setMessage("Delete failed.");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Blogs</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Create and edit website articles.
        </p>
      </div>
      {message && (
        <div className="rounded-xl bg-primary-50 text-primary-800 text-sm px-4 py-3">
          {message}
        </div>
      )}

      <form
        onSubmit={save}
        className="rounded-2xl border bg-white dark:bg-neutral-900 dark:border-neutral-800 p-5 space-y-3"
      >
        <h2 className="font-bold">{editId ? "Edit article" : "New article"}</h2>
        <input
          required
          placeholder="Title"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="w-full h-11 px-4 rounded-xl border"
        />
        <div className="grid sm:grid-cols-2 gap-3">
          <input
            required
            placeholder="Author"
            value={form.author}
            onChange={(e) => setForm({ ...form, author: e.target.value })}
            className="h-11 px-4 rounded-xl border"
          />
          <input
            type="datetime-local"
            required
            value={form.published_date}
            onChange={(e) =>
              setForm({ ...form, published_date: e.target.value })
            }
            className="h-11 px-4 rounded-xl border"
          />
        </div>
        <textarea
          required
          rows={8}
          placeholder="HTML content supported"
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          className="w-full px-4 py-3 rounded-xl border font-mono text-sm"
        />
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={loading}
            className="h-10 px-4 rounded-xl bcr-btn-primary text-sm font-semibold"
          >
            {editId ? "Update" : "Publish"}
          </button>
          {editId && (
            <button
              type="button"
              onClick={() => {
                setEditId(null);
                setForm(empty);
              }}
              className="h-10 px-4 rounded-xl border text-sm"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="rounded-2xl border bg-white dark:bg-neutral-900 dark:border-neutral-800 overflow-hidden">
        <ul className="divide-y dark:divide-neutral-800">
          {blogs.map((b) => (
            <li
              key={b.id}
              className="px-5 py-4 flex items-start justify-between gap-3"
            >
              <div className="min-w-0">
                <p className="font-semibold truncate">{b.title}</p>
                <p className="text-xs text-neutral-500 mt-1">
                  {b.author} · {new Date(b.published_date).toLocaleString()}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    setEditId(b.id);
                    setForm({
                      title: b.title,
                      content: b.content,
                      author: b.author,
                      published_date: new Date(b.published_date)
                        .toISOString()
                        .slice(0, 16),
                    });
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="p-2 rounded-lg hover:bg-neutral-100"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteId(b.id)}
                  className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <ConfirmDialog
        open={!!deleteId}
        title="Delete blog?"
        message="This article will be removed from the website."
        loading={loading}
        onClose={() => setDeleteId(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AdminBlogs;
